// components/layout/Header.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, toggleLanguage } = useLanguage();
    const { t } = useTranslation(language);
    const [hoveredItem, setHoveredItem] = useState(null);
    const { isAuthenticated, user, userProfile, login, logout } = useApp();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= 769 : false
    );
    const userMenuRef = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    useEffect(() => {
        const onResize = () => setIsDesktop(window.innerWidth >= 769);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const menuItems = [
        {
            path: '/home',
            icon: `${process.env.PUBLIC_URL}/img/home.svg`,
            title: t('nav.home'),
            short: t('nav.home'),
            alt: 'Home'
        },
        {
            path: '/similar',
            icon: `${process.env.PUBLIC_URL}/img/similar.svg`,
            title: t('nav.similar'),
            short: t('nav.similarShort'),
            state: { type: 'similar' },
            alt: 'Similar'
        },
        {
            path: '/daily-discovery',
            icon: `${process.env.PUBLIC_URL}/img/daily.svg`,
            title: isDesktop ? t('nav.approfondimenti') : t('nav.daily'),
            short: isDesktop ? t('nav.approfondimenti') : t('nav.dailyShort'),
            state: isDesktop
                ? { type: 'daily-discovery', showWall: true }
                : { type: 'daily-discovery' },
            alt: 'Daily'
        },
        {
            path: '/random',
            icon: `${process.env.PUBLIC_URL}/img/random.svg`,
            title: t('nav.random'),
            short: t('nav.random'),
            state: { type: 'random' },
            alt: 'Random'
        },
        {
            path: '/coffee',
            icon: `${process.env.PUBLIC_URL}/img/local_cafe_24dp_1F1F1F.svg`,
            title: t('nav.coffee'),
            short: t('nav.coffee'),
            alt: 'Coffee'
        }
    ];

    const isActive = (path) => {
        if (path === '/daily-discovery') {
            return location.pathname === '/daily-discovery' ||
                   location.pathname === '/daily-discovery-artists';
        }
        return location.pathname === path;
    };

    return (
        <header className="header">
            <div className="header-content">
                <div className="header-brand">
                    <button
                        onClick={() => navigate('/home')}
                        className="logo"
                        type="button"
                        title={t('nav.home')}
                    >
                        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Foundly" />
                    </button>

                    <button
                        onClick={toggleLanguage}
                        className="lang-toggle"
                        type="button"
                        title={language === 'it' ? 'Switch to English' : 'Passa all\'italiano'}
                    >
                        {language === 'it' ? 'IT' : 'EN'}
                    </button>
                </div>

                <nav className="header-nav">
                    {menuItems.map(item => {
                        const active = isActive(item.path);
                        const isHovered = hoveredItem === item.path;

                        return (
                            <button
                                key={item.path}
                                type="button"
                                className={`header-nav-item ${active ? 'is-active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.state) {
                                        navigate(item.path, {
                                            state: {
                                                ...item.state,
                                                forceReset: true,
                                                timestamp: Date.now()
                                            }
                                        });
                                    } else {
                                        navigate(item.path);
                                    }
                                }}
                                onMouseEnter={() => setHoveredItem(item.path)}
                                onMouseLeave={() => setHoveredItem(null)}
                                title={item.title}
                            >
                                <img
                                    src={item.icon}
                                    alt={item.alt}
                                    className="header-nav-icon"
                                    style={{
                                        opacity: active ? 1 : (isHovered ? 0.85 : 0.55)
                                    }}
                                />
                            </button>
                        );
                    })}

                    <div className="auth-container" ref={userMenuRef}>
                        {isAuthenticated ? (
                            <div className="user-menu">
                                <button
                                    className="user-btn"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsUserMenuOpen((prev) => !prev);
                                    }}
                                    title={userProfile?.display_name || user?.email || 'Account'}
                                >
                                    {userProfile?.images?.[0]?.url || user?.user_metadata?.picture ? (
                                        <img
                                            src={userProfile?.images?.[0]?.url || user?.user_metadata?.picture}
                                            alt="avatar"
                                            className="user-avatar"
                                        />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            {(userProfile?.display_name || user?.user_metadata?.full_name || 'U')
                                                .slice(0, 1)
                                                .toUpperCase()}
                                        </div>
                                    )}
                                </button>
                                {isUserMenuOpen && (
                                    <div className="dropdown-menu">
                                        <button
                                            className="dropdown-item"
                                            type="button"
                                            onClick={() => {
                                                setIsUserMenuOpen(false);
                                                navigate('/dashboard');
                                            }}
                                        >
                                            <span>Dashboard</span>
                                        </button>
                                        <button
                                            className="dropdown-item"
                                            type="button"
                                            onClick={() => {
                                                setIsUserMenuOpen(false);
                                                navigate('/favorites');
                                            }}
                                        >
                                            <span>Preferiti</span>
                                        </button>
                                        <div className="dropdown-divider" />
                                        <button
                                            className="dropdown-item"
                                            type="button"
                                            onClick={async () => {
                                                setIsUserMenuOpen(false);
                                                await logout();
                                                navigate('/home', { replace: true });
                                            }}
                                        >
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button className="login-icon-btn" type="button" onClick={login} title="Login">
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt="G"
                                    style={{ width: 18, height: 18 }}
                                />
                            </button>
                        )}
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;
